import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { UserCircle, Lock, Save, Trash2, Mail, Phone, Edit } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Profile() {
  const navigate = useNavigate();
  const [user, setUser] = useState(JSON.parse(localStorage.getItem("user") || "{}"));
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    full_name: user.full_name || "",
    email: user.email || "",
    contact_number: user.contact_number || ""
  });
  const [passwordData, setPasswordData] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });
  const [loading, setLoading] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);

  useEffect(() => {
    fetchUserProfile();
  }, []);

  const fetchUserProfile = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const userData = response.data;
      setUser(userData);
      setFormData({
        full_name: userData.full_name || "",
        email: userData.email || "",
        contact_number: userData.contact_number || ""
      });
    } catch (error) {
      console.log("Could not refresh profile");
    }
  };

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.put(`${API}/users/${user.id}/profile`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update local storage
      const updatedUser = response.data.user;
      localStorage.setItem("user", JSON.stringify(updatedUser));
      setUser(updatedUser);
      setIsEditing(false);
      toast.success("Profile updated successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update profile");
    } finally {
      setSavingProfile(false);
    }
  };

  const handleDeleteAccount = async () => {
    const confirmed = window.confirm(
      "⚠️ WARNING: This will permanently delete your account and ALL associated data including:\n\n" +
      "• Your profile information\n" +
      "• All your properties\n" +
      "• All your leads\n" +
      "• All your documents\n" +
      "• All your notifications\n\n" +
      "This action CANNOT be undone. Are you sure you want to proceed?"
    );
    
    if (!confirmed) return;
    
    const finalConfirm = window.prompt("Type DELETE to confirm account deletion:");
    if (finalConfirm !== "DELETE") {
      toast.error("Account deletion cancelled");
      return;
    }
    
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/users/${user.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Clear local storage and redirect to login
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      toast.success("Account deleted successfully");
      navigate("/login");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete account");
    }
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error("New passwords do not match");
      return;
    }

    if (passwordData.new_password.length < 6) {
      toast.error("Password must be at least 6 characters long");
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/auth/change-password`,
        {
          current_password: passwordData.current_password,
          new_password: passwordData.new_password,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      toast.success("Password changed successfully!");
      setPasswordData({
        current_password: "",
        new_password: "",
        confirm_password: "",
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setLoading(false);
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case "admin": return "bg-amber-100 text-amber-800";
      case "team_leader": return "bg-purple-100 text-purple-800";
      case "sourcing_manager": return "bg-cyan-100 text-cyan-800";
      case "agent": return "bg-blue-100 text-blue-800";
      default: return "bg-green-100 text-green-800";
    }
  };

  const formatRoleName = (role) => {
    switch (role) {
      case "team_leader": return "Team Leader";
      case "sourcing_manager": return "Sourcing Manager";
      default: return role;
    }
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto fade-in space-y-6">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-[#0f2744]" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Profile Settings
          </h1>
          <p className="text-slate-500 mt-1">
            Manage your account settings, profile information, and password
          </p>
        </div>

        {/* Profile Information */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="flex items-center justify-between p-4 bg-slate-50 border-b border-slate-200">
            <div className="flex items-center gap-3">
              <UserCircle className="h-5 w-5 text-[#0f2744]" />
              <h2 className="font-semibold text-[#0f2744]">Profile Information</h2>
            </div>
            {!isEditing ? (
              <Button
                onClick={() => setIsEditing(true)}
                variant="outline"
                size="sm"
                className="border-[#0f2744] text-[#0f2744]"
                data-testid="edit-profile-btn"
              >
                <Edit className="h-4 w-4 mr-1" />
                Edit
              </Button>
            ) : (
              <div className="flex gap-2">
                <Button
                  onClick={() => {
                    setIsEditing(false);
                    setFormData({
                      full_name: user.full_name || "",
                      email: user.email || "",
                      contact_number: user.contact_number || ""
                    });
                  }}
                  variant="ghost"
                  size="sm"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveProfile}
                  disabled={savingProfile}
                  size="sm"
                  className="bg-[#c9a227] hover:bg-[#ae8220] text-white"
                  data-testid="save-profile-btn"
                >
                  <Save className="h-4 w-4 mr-1" />
                  {savingProfile ? "Saving..." : "Save"}
                </Button>
              </div>
            )}
          </div>
          
          <div className="p-6">
            {!isEditing ? (
              <div className="flex items-center gap-4">
                <div className="h-20 w-20 rounded-full bg-[#0f2744]/10 flex items-center justify-center">
                  <span className="text-3xl font-bold text-[#0f2744]">
                    {user.full_name?.charAt(0)?.toUpperCase() || "U"}
                  </span>
                </div>
                <div>
                  <h3 className="text-2xl font-semibold text-[#0f2744]">{user.full_name}</h3>
                  <div className="flex items-center gap-2 text-slate-500 mt-1">
                    <Mail className="h-4 w-4" />
                    <span>{user.email}</span>
                  </div>
                  {user.contact_number && (
                    <div className="flex items-center gap-2 text-slate-500 mt-1">
                      <Phone className="h-4 w-4" />
                      <span>{user.contact_number}</span>
                    </div>
                  )}
                  <span className={`mt-2 inline-block text-xs px-3 py-1 rounded-full uppercase font-bold tracking-wide ${getRoleBadgeColor(user.role)}`}>
                    {formatRoleName(user.role)}
                  </span>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <Label htmlFor="full_name">Full Name</Label>
                  <Input
                    id="full_name"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleProfileChange}
                    className="mt-1"
                    data-testid="input-full-name"
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleProfileChange}
                    className="mt-1"
                    data-testid="input-email"
                  />
                </div>
                <div>
                  <Label htmlFor="contact_number">Contact Number</Label>
                  <Input
                    id="contact_number"
                    name="contact_number"
                    value={formData.contact_number}
                    onChange={handleProfileChange}
                    className="mt-1"
                    placeholder="+91 9876543210"
                    data-testid="input-contact"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Change Password */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="flex items-center gap-3 p-4 bg-slate-50 border-b border-slate-200">
            <Lock className="h-5 w-5 text-[#0f2744]" />
            <h2 className="font-semibold text-[#0f2744]">Change Password</h2>
          </div>

          <form onSubmit={handlePasswordSubmit} className="p-6 space-y-4">
            <div>
              <Label htmlFor="current_password">Current Password *</Label>
              <Input
                id="current_password"
                name="current_password"
                type="password"
                data-testid="current-password-input"
                value={passwordData.current_password}
                onChange={handlePasswordChange}
                className="mt-1"
                required
                placeholder="Enter current password"
              />
            </div>

            <div>
              <Label htmlFor="new_password">New Password *</Label>
              <Input
                id="new_password"
                name="new_password"
                type="password"
                data-testid="new-password-input"
                value={passwordData.new_password}
                onChange={handlePasswordChange}
                className="mt-1"
                required
                placeholder="Enter new password (min 6 characters)"
              />
            </div>

            <div>
              <Label htmlFor="confirm_password">Confirm New Password *</Label>
              <Input
                id="confirm_password"
                name="confirm_password"
                type="password"
                data-testid="confirm-password-input"
                value={passwordData.confirm_password}
                onChange={handlePasswordChange}
                className="mt-1"
                required
                placeholder="Confirm new password"
              />
            </div>

            <Button
              data-testid="change-password-button"
              type="submit"
              disabled={loading}
              className="bg-[#0f2744] hover:bg-[#1e3a5f] text-white"
            >
              {loading ? "Changing..." : "Change Password"}
            </Button>
          </form>
        </div>

        {/* Danger Zone - Delete Account */}
        <div className="bg-white rounded-xl border border-[#0f2744]/30 shadow-sm overflow-hidden">
          <div className="p-4 bg-[#0f2744]/5 border-b border-[#0f2744]/20">
            <h2 className="font-semibold text-[#0f2744]">Danger Zone</h2>
          </div>
          <div className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium text-slate-700">Delete Account</h3>
                <p className="text-sm text-slate-500 mt-1">
                  Permanently delete your account and all associated data including properties, leads, and documents.
                </p>
              </div>
              <Button
                onClick={handleDeleteAccount}
                variant="outline"
                className="border-[#0f2744] text-[#0f2744] hover:bg-[#0f2744] hover:text-white"
                data-testid="delete-account-btn"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Account
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
